import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useMainContext } from '../../hooks/useMainContext';
import { Spin, Modal, message, Button } from 'antd';
import {
    LoadingOutlined,
    DeleteOutlined,
    FolderOpenOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { Searchbar } from './Searchbar';
import { getIdToken } from '../../utils/getIdToken';
import { SERVER_BASE_URL } from '../../__common/env';

export const ProjectList = observer(() => {
    const { viewManager, operationsManager } = useMainContext();
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [openingId, setOpeningId] = useState(null);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        viewManager.fetchProjects();

        const query = window.location.search;
        const params = new URLSearchParams(query);
        const projectId = params.get('project_id');

        if (projectId && !loading && !openingId) {
            handleOpenProject(projectId);
        }
    }, [viewManager, loading, openingId]);

    function updateQueryParam(key, value) {
        const url = new URL(window.location.href);
        if (value === null || value === undefined) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
        window.history.pushState({}, '', url);
    }

    const handleOpenProject = async (projectId, e) => {
        if (e) e.stopPropagation();
        if (loading || openingId === projectId) return;

        setOpeningId(projectId);
        setLoading(true);

        try {
            viewManager.setProjectId(projectId);
            updateQueryParam('project_id', projectId);
            await viewManager.openProject();
        } catch (error) {
            console.error('Error opening project:', error);
            message.error('Failed to open project. Please try again.');
        } finally {
            setLoading(false);
            setOpeningId(null);
        }
    };

    const confirmDelete = (projectId, e) => {
        e.stopPropagation();
        Modal.confirm({
            title: 'Delete Project',
            content: 'Are you sure you want to delete this project?',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => handleDeleteProject(projectId),
        });
    };

    const handleDeleteProject = async (projectId) => {
        if (!projectId) return;

        setDeletingId(projectId);
        try {
            const token = await getIdToken();
            const response = await fetch(
                `${SERVER_BASE_URL}/project/${projectId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            const data = await response.json();

            if (!response.ok) {
                message.error(data.error);
            } else {
                message.success(data.message);
            }

            await viewManager.fetchProjects();
        } catch (error) {
            console.error('Error deleting project:', error);
            message.error('Error deleting project. Please try again.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleReload = () => {
        viewManager.fetchProjects();
    };

    // Filter projects
    const filteredProjects = viewManager.projects
        .slice()
        .filter((project) =>
            project.name.toLowerCase().includes(searchValue.toLowerCase()),
        );

    if (viewManager.isLoading || loading) {
        return (
            <div className="h-[calc(100vh-244px)] flex justify-center items-center">
                <Spin
                    indicator={
                        <LoadingOutlined
                            style={{ fontSize: 48, color: '#f46147' }}
                            spin
                        />
                    }
                />
            </div>
        );
    }

    if (viewManager.hasProjectsError) {
        return (
            <div className="flex flex-col">
                <Searchbar value={searchValue} onChange={setSearchValue} />
                <div className="h-[calc(100vh-292px)] flex flex-col justify-center items-center gap-4">
                    <p className="text-lg text-gray-600">
                        Failed to load projects. Please try again.
                    </p>
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={handleReload}
                        style={{
                            backgroundColor: '#cccccc',
                            color: 'black',
                            border: 'none',
                        }}
                        className="hover:!bg-[#f56147] hover:!text-white transition-colors duration-300">
                        Reload Projects
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <Searchbar value={searchValue} onChange={setSearchValue} />
            {filteredProjects.length === 0 ? (
                <div className="h-[calc(100vh-292px)] flex flex-col justify-center items-center gap-4">
                    <p className="text-lg text-gray-600">
                        No such projects found. Please create a new project.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-6 gap-[24px] mt-[24px] h-[calc(100vh-292px)] overflow-y-scroll">
                    {filteredProjects.map((project) => (
                        <div
                            key={project.id}
                            className="relative flex flex-col gap-[16px] p-2 rounded-lg border-2 border-transparent hover:bg-gray-100 transition-all h-fit"
                            onDoubleClick={() => handleOpenProject(project.id)}>
                            <div className="border-[2px] border-theme-button-bg rounded-[8px] cursor-pointer overflow-hidden">
                                <img
                                    className="hover:scale-105 duration-300 ease-in-out rounded-[8px] w-full"
                                    src={
                                        project.imgUrl
                                            ? `${SERVER_BASE_URL}/assets/img/${project.imgUrl}`
                                            : '/assets/img/project.png'
                                    }
                                    alt={project.name}
                                />
                            </div>
                            <p className="font-favorit text-[16px] leading-[16px] text-theme-black font-normal">
                                {project.name}
                            </p>
                            <div className="absolute top-2 right-2 flex gap-2">
                                <button
                                    onClick={(e) =>
                                        handleOpenProject(project.id, e)
                                    }
                                    className="p-2 rounded-full flex justify-center items-center h-[40px] w-[40px] bg-white bg-opacity-80 hover:bg-orange-100 transition-colors"
                                    disabled={openingId === project.id}>
                                    {openingId === project.id ? (
                                        <LoadingOutlined className="text-orange-500 text-base" />
                                    ) : (
                                        <FolderOpenOutlined className="text-orange-500 text-base" />
                                    )}
                                </button>
                                <button
                                    onClick={(e) =>
                                        confirmDelete(project.id, e)
                                    }
                                    className="p-2 rounded-full flex justify-center items-center h-[40px] w-[40px] bg-white bg-opacity-80 hover:bg-red-100 transition-colors"
                                    disabled={deletingId === project.id}>
                                    {deletingId === project.id ? (
                                        <LoadingOutlined className="text-red-500 text-base" />
                                    ) : (
                                        <DeleteOutlined className="text-red-500 text-base" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default ProjectList;
